import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarModule } from '@angular/material/snack-bar';
import { CurrentUser } from '../../../common/models/user';
import { StorageService } from '../../../common/services/storage.service';
import { SearchService } from '../../../common/services/search.service';
import { CustomizerSettingsService } from '../../../customizer-settings/customizer-settings.service';
import { AllocationService } from '../../../common/services/allocation.service';
import { AllocationFormType, AllocationSummary, GetAllocationsFilter } from '../../../common/models/allocation';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FilterService } from '../../../common/components/filter/services/filter.service';
import { Subject, takeUntil } from 'rxjs';
import { AllocationsFilterBuilder } from '../../../common/components/filter/builders/allocations-filter-builder';
import { AllocationStatus } from '../../../common/enums/allocation-status';
import { GenericDialogComponent } from '../../../common/components/dialog/dialog';

@Component({
    selector: 'app-allocations',
    templateUrl: './allocations.component.html',
    styleUrl: './allocations.component.scss',
    imports: [MatCardModule, MatMenuModule, MatButtonModule, MatPaginatorModule, MatTooltipModule, MatIconModule, MatDialogModule, MatSnackBarModule, MatCheckboxModule, CommonModule],
    standalone: true
})
export class AllocationsComponent implements OnInit {
    private destroy$ = new Subject<void>();

    allocations: AllocationSummary[] = [];
    filteredAllocations: AllocationSummary[] = [];
    public AllocationStatus = AllocationStatus;
    public AllocationFormType = AllocationFormType;
    user: CurrentUser | null = null;
    isToggled = false;

    private readonly snackbarConfig: MatSnackBarConfig = {
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top'
    };

    constructor(
      private router: Router,
      private filterBuilder: AllocationsFilterBuilder,
      private allocationService: AllocationService,
      private storageService: StorageService,
      private searchService: SearchService,
      private filterService: FilterService,
      public themeService: CustomizerSettingsService,
      private snackBar: MatSnackBar,
      private dialog: MatDialog
    ) {}
  
    ngOnInit() {
      this.themeService.isToggled$.pipe(takeUntil(this.destroy$)).subscribe(isToggled => {
        this.isToggled = isToggled;
      });   
      this.searchService.currentSearchTerm$.pipe(takeUntil(this.destroy$)).subscribe(term => {
        this.applySearch(term);
      });
      this.filterService.filterChanges$.pipe(takeUntil(this.destroy$)).subscribe((filters) => {
        this.filterService.setFiltersInScope(filters);
        this.fetchAllocations();
      });
      this.storageService.user$.pipe(takeUntil(this.destroy$)).subscribe(user => {
        this.user = user;
        this.initializeFilters();
      });
    }

    ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
    }

    initializeFilters(): void {
      const startYear = 2025;
      const currentYear = new Date().getFullYear();
    
      const config = this.filterBuilder
        .reset()
        .years(startYear)
        .status()
        .build();

      this.filterService.setFilterConfig(config);
      this.filterService.restoreFiltersInScope({ year: currentYear });
      
    }

    fetchAllocations(): void {
        const savedFilterValues = this.filterService.getFiltersInScope();

        const filter = new GetAllocationsFilter(
          this.user?.municipalityId, 
          undefined, 
          savedFilterValues?.status, 
          savedFilterValues?.year
        );

        this.allocationService.getAllocations(filter).pipe(takeUntil(this.destroy$)).subscribe({
          next: (data) => {
            this.allocations = data.map(a => AllocationSummary.fromJson(a));
            this.filteredAllocations = [...this.allocations];
          },
          error: (error) => {
            console.error('Greška prilikom dohvata izravnih dodjela', error);
          }
        });
    }

    applySearch(searchTerm: string): void {
      searchTerm = searchTerm.trim().toLowerCase();
      if (searchTerm) {
        this.filteredAllocations = this.allocations.filter((allocation: AllocationSummary) => {
          return (
            allocation.organizationName.toLowerCase().includes(searchTerm) ||
            allocation.activityName.toLowerCase().includes(searchTerm)
          );
        });
      } else {
        this.filteredAllocations = [...this.allocations];
      }
    }

    confirmReturn(allocation: AllocationSummary): void {
      const dialogRef = this.dialog.open(GenericDialogComponent, {
        width: '50%',
        data: {
          title: 'Potvrdite vraćanje',
          message: `Jeste li sigurni da želite vratiti izravnu dodjelu "${allocation.activityName}" na doradu?`,
          confirmButtonText: 'Da',
          cancelButtonText: 'Ne',
        },
      });
  
      dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
        if (result === true) {
          this.returnAllocation(allocation);
        }
      });
    }

    private returnAllocation(allocation: AllocationSummary): void {
      this.allocationService.changeStatus(allocation.id, AllocationStatus.Returned).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.snackBar.open('Izravna dodjela je vraćena u obradu', 'Zatvori', this.snackbarConfig);
          this.fetchAllocations();
        },
        error: (error) => {
          console.error('Greška prilikom vraćanja izravne dodjele u obradu', error);
        }
      });
    }

    confirmApprove(allocation: AllocationSummary): void {
      const dialogRef = this.dialog.open(GenericDialogComponent, {
        width: '50%',
        data: {
          title: 'Potvrdite odobravanje',
          message: `Jeste li sigurni da želite odobriti izravnu dodjelu "${allocation.activityName}"?`,
          confirmButtonText: 'Da',
          cancelButtonText: 'Ne',
        },
      });
  
      dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
        if (result === true) {
          this.approveAllocation(allocation);
        }
      });
    }

    private approveAllocation(allocation: AllocationSummary): void {
      this.allocationService.changeStatus(allocation.id, AllocationStatus.Approved).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.snackBar.open('Izravna dodjela je odobrena', 'Zatvori', this.snackbarConfig);
          this.fetchAllocations();
        },
        error: (error) => {
          console.error('Greška prilikom odobravanja izravne dodjele', error);
        }
      });
    }

    confirmReject(allocation: AllocationSummary): void {
      const dialogRef = this.dialog.open(GenericDialogComponent, {
        width: '50%',
        data: {
          title: 'Potvrdite odbijanje',
          message: `Jeste li sigurni da želite odbiti izravnu dodjelu "${allocation.activityName}"?`,
          confirmButtonText: 'Da',
          cancelButtonText: 'Ne',
        },
      });
  
      dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
        if (result === true) {
          this.rejectAllocation(allocation);
        }
      });
    }

    private rejectAllocation(allocation: AllocationSummary): void {
      this.allocationService.changeStatus(allocation.id, AllocationStatus.Rejected).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.snackBar.open('Izravna dodjela je odbijena', 'Zatvori', this.snackbarConfig);
          this.fetchAllocations();
        },
        error: (error) => {
          console.error('Greška prilikom odbacivanja izravne dodjele', error);
        }
      });
    }

    downloadForms(allocation: AllocationSummary, formTypes: AllocationFormType[]): void {
      this.allocationService.downloadForms(allocation.id, formTypes).pipe(takeUntil(this.destroy$)).subscribe({
          next: (blob) => {
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'dokumentacija-izravna-dodjela-sredstava.zip';
              a.click();
              window.URL.revokeObjectURL(url);
          },
          error: (err) => {
              console.error('Error downloading allocation forms', err);
          }
      });
    }

    goToViewAllocation(allocation: AllocationSummary): void {
      this.router.navigate(['municipality', 'allocations', allocation.id.toString(), 'view']);
    }

    goToCreateProject(allocation: AllocationSummary): void {
      this.router.navigate(['municipality', 'projects', allocation.id.toString(), 'create'], 
      { state: { programProjectName: allocation.activityName }});
    }
}
